// src/features/places/MarketTilesView.jsx
//
// PLACES-SLICE-2: Markets lens Tiles view. City cards at market grain using
// the same card silhouette as the Offices LocationTile (photo/gradient
// header, overlay pill, name row, meta line). Office counts come from the
// canonical inventory once loaded; the plan composition is the fallback.

import React from "react";
import { T } from "../../app/tokens.js";
import { countedMarketOffices } from "./marketInventory.js";
import { isCountedMarketOffice } from "../plan/marketsService.js";

const GRAD = { AMER: T.photoGradAmer, EMEA: T.photoGradEmea, APAC: T.photoGradApac };

export function marketOfficeCount(market, locations, inventoryReady) {
  if (inventoryReady) return countedMarketOffices(locations, market.id).length;
  return (market.locations ?? []).filter(isCountedMarketOffice).length;
}

export function peopleSeatsLabel(market) {
  const hc = market.totalHeadcount;
  const seats = market.totalSeats;
  if (hc == null && seats == null) return "No people or seat data";
  const pct = hc != null && seats > 0 ? ` · ${Math.round((hc / seats) * 100)}%` : "";
  return `${hc != null ? Number(hc).toLocaleString() : "—"} / ${seats != null ? Number(seats).toLocaleString() : "—"}${pct}`;
}

export function MarketTilesView({ markets, locations, inventoryReady, onOpenMarket, renderStrategy }) {
  if (markets.length === 0) {
    return (
      <div data-testid="market-tiles-empty" style={{ padding: "40px 0", textAlign: "center", color: T.mid, fontSize: T.fsSm }}>
        No markets match the current search and filters.
      </div>
    );
  }
  return (
    <div data-testid="market-tiles" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16, paddingTop: 4 }}>
      {markets.map((m) => {
        const count = marketOfficeCount(m, locations, inventoryReady);
        const over = m.totalHeadcount != null && m.totalSeats != null && m.totalHeadcount > m.totalSeats;
        return (
          <div
            key={m.id}
            role="button"
            tabIndex={0}
            data-testid={`market-tile-${m.id}`}
            aria-label={`${m.name} - ${count} offices`}
            className="places-market-tile"
            onClick={() => onOpenMarket(m.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenMarket(m.id); } }}
            style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden", cursor: "pointer", boxShadow: T.cardShadow, opacity: m.status === "Exited" ? 0.6 : 1 }}
          >
            <div style={{ position: "relative", height: 112, background: m.photoUrl ? undefined : (GRAD[m.region] ?? T.photoGradAmer), backgroundImage: m.photoUrl ? `url(${m.photoUrl})` : undefined, backgroundSize: "cover", backgroundPosition: "center" }}>
              <div style={{ position: "absolute", top: 8, left: 8 }}>{renderStrategy?.(m.strategy)}</div>
              {!m.photoUrl && (
                <span aria-hidden="true" style={{ position: "absolute", right: 14, bottom: 6, fontSize: T.fs3xl, fontWeight: T.fwBold, color: T.whiteHalf, letterSpacing: 1 }}>
                  {(m.name ?? m.id).slice(0, 3).toUpperCase()}
                </span>
              )}
            </div>
            <div style={{ padding: "11px 14px 13px" }}>
              <div data-testid={`market-tile-name-${m.id}`} style={{ fontWeight: T.fwBold, fontSize: T.fsBase, color: T.dark }}>{m.name ?? m.id}</div>
              <div data-testid={`market-tile-meta-${m.id}`} style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3 }}>
                {[m.region, `${count} ${count === 1 ? "office" : "offices"}`].filter(Boolean).join(" · ")}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: T.fsXs }}>
                <span style={{ color: T.light, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".05em" }}>People / seats</span>
                <span data-testid={`market-tile-people-${m.id}`} style={{ fontWeight: T.fwBold, color: over ? T.ragRed : T.dark, fontVariantNumeric: "tabular-nums" }}>{peopleSeatsLabel(m)}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
